import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { animate, createTimeline, stagger, utils } from "animejs";
import { Reveal, FloatingOrb, StarField } from "../motion/space-primitives";
import { FAMILIES, FEATURED_CHAINS, GATEWAY, STATS } from "../../data/apiReference";
import { SERVICES } from "../../data/services";
import ChainLogo from "../api/ChainLogo";

const REQUEST = `curl -s -X POST ${GATEWAY.rpc} \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "x-chain: eth" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","id":1,"method":"eth_blockNumber","params":[]}'`;
const RESPONSE = `{ "jsonrpc": "2.0", "id": 1, "result": "0x189a851" }`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Fires `cb` once when the element scrolls into view.
const useInView = (ref, cb, threshold = 0.3) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          cb();
          io.disconnect();
        }
      },
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// ---------------------------------------------------------------------------
// Service card
// ---------------------------------------------------------------------------
const ServiceCard = ({ service, index }) => {
  const isDatanodes = service.id === "datanodes";
  const featuredLogos = isDatanodes ? service.networks.filter((n) => service.featured.includes(n.slug)) : service.networks;
  const total = service.networks.length;

  return (
    <Reveal delay={0.08 + index * 0.06} className="h-full">
      <article
        className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20"
        style={{ "--accent": service.accent }}
      >
        <span className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-30 blur-3xl" style={{ backgroundColor: service.accent }} />

        {/* Header */}
        <div className="relative flex items-start gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
            {service.logo ? (
              <img src={service.logo} alt="" aria-hidden="true" className="h-7 w-7 object-contain" />
            ) : (
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke={service.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 17l5-6 4 4 6-8 3 3" />
                <path d="M3 21h18" />
              </svg>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="m-0 text-lg font-bold leading-tight text-white">{service.name}</h3>
            <p className="m-0 mt-0.5 text-xs text-gray-400">{service.tagline}</p>
          </div>
        </div>

        <p className="relative mb-0 mt-4 text-sm leading-relaxed text-gray-300">{service.description}</p>

        {/* Service information */}
        <dl className="relative m-0 mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
          {service.facts.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="text-gray-500">{k}</dt>
              <dd className="m-0 break-words font-mono text-[11px] text-gray-200">{v}</dd>
            </React.Fragment>
          ))}
        </dl>

        {/* Networks */}
        <div className="relative mt-5 border-t border-white/10 pt-4">
          <div className="mb-2 flex items-baseline justify-between">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500">Networks</p>
            <span className="font-mono text-[11px] text-gray-400">
              {total} {total === 1 ? "network" : "networks"}
            </span>
          </div>

          {isDatanodes ? (
            <>
              <div className="flex flex-wrap gap-1.5">
                {featuredLogos.map((n) => (
                  <Link key={n.slug} to={n.href} className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] py-1 pl-1 pr-2 text-xs text-gray-200 no-underline hover:border-primary/40">
                    <ChainLogo chain={n} size={18} />
                    {n.name}
                  </Link>
                ))}
              </div>
              <p className="m-0 mt-2 text-[11px] text-gray-500">
                <Link to="/networks" className="text-primary no-underline hover:underline">
                  all {service.networks.length} networks →
                </Link>
              </p>
            </>
          ) : (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {service.networks.map((n) => {
                const inner = (
                  <>
                    <ChainLogo chain={n} size={18} />
                    <span className="text-gray-200">{n.name}</span>
                    {n.detail && <span className="font-mono text-[10px] text-gray-500">{n.detail}</span>}
                  </>
                );
                return (
                  <li key={n.name}>
                    {n.href ? (
                      <a href={n.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] py-1 pl-1 pr-2 text-xs no-underline hover:border-primary/40">
                        {inner}
                      </a>
                    ) : (
                      <span className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.03] py-1 pl-1 pr-2 text-xs">{inner}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="relative mt-auto flex items-center justify-end gap-3 pt-5">
          {service.docs.startsWith("/") ? (
            <Link to={service.docs} className="shrink-0 text-xs font-semibold no-underline hover:underline" style={{ color: service.accent }}>
              {service.docsLabel} →
            </Link>
          ) : (
            <a href={service.docs} target="_blank" rel="noopener noreferrer" className="shrink-0 text-xs font-semibold no-underline hover:underline" style={{ color: service.accent }}>
              {service.docsLabel} →
            </a>
          )}
        </div>
      </article>
    </Reveal>
  );
};

// ---------------------------------------------------------------------------
// Terminal: the request types itself, then the response drops in (anime.js)
// ---------------------------------------------------------------------------
const Terminal = () => {
  const ref = useRef(null);
  const [typed, setTyped] = useState(prefersReducedMotion() ? REQUEST : "");
  const [showResponse, setShowResponse] = useState(prefersReducedMotion());

  useInView(ref, () => {
    if (prefersReducedMotion()) return;
    const state = { i: 0 };
    const tl = createTimeline({ defaults: { ease: "linear" } });
    tl.add(state, {
      i: REQUEST.length,
      duration: REQUEST.length * 14,
      modifier: utils.round(0),
      onUpdate: () => setTyped(REQUEST.slice(0, state.i)),
    }).add({}, { duration: 260, onComplete: () => setShowResponse(true) });
  });

  useEffect(() => {
    if (!showResponse || prefersReducedMotion()) return;
    const el = ref.current?.querySelector(".api-terminal-response");
    if (el) animate(el, { opacity: [0, 1], translateY: [6, 0], duration: 420, ease: "outCubic" });
  }, [showResponse]);

  return (
    <div ref={ref} className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A14] shadow-2xl">
      <div className="flex items-center gap-2 border-b border-white/[0.06] px-4 py-3">
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-primary/70" />
        <span className="ml-3 font-mono text-[11px] uppercase tracking-[0.16em] text-gray-500">rpc.datanodes.nodefleet.net</span>
      </div>
      <pre className="m-0 min-h-[150px] overflow-x-auto p-5 font-mono text-[13px] leading-relaxed text-gray-200">
        <code>
          {typed}
          {!showResponse && <span className="ml-0.5 inline-block h-[1em] w-[7px] translate-y-[2px] bg-primary align-middle" style={{ animation: "star-twinkle 0.9s steps(2) infinite" }} />}
        </code>
      </pre>
      <div className="api-terminal-response border-t border-white/[0.06] bg-white/[0.02] px-5 py-4" style={{ opacity: showResponse ? 1 : 0 }}>
        <p className="m-0 mb-1 font-mono text-[11px] uppercase tracking-[0.16em] text-primary/80">Response</p>
        <pre className="m-0 overflow-x-auto font-mono text-[13px] text-primary-100">
          <code>{RESPONSE}</code>
        </pre>
      </div>
    </div>
  );
};

// Stat tiles count up into view.
const HOME_STATS = [
  { to: STATS.networks, suffix: "", label: "Networks on Datanodes" },
  { to: SERVICES.length, suffix: "", label: "Network services" },
  { to: STATS.interfaces.length, suffix: "", label: "Interfaces" },
];

const Stats = () => {
  const ref = useRef(null);
  useInView(ref, () => {
    const nodes = ref.current?.querySelectorAll("[data-count]") || [];
    nodes.forEach((node, i) => {
      const to = Number(node.dataset.count);
      if (prefersReducedMotion()) {
        node.textContent = String(to);
        return;
      }
      const obj = { v: 0 };
      animate(obj, {
        v: to,
        duration: 1300,
        delay: i * 120,
        ease: "outExpo",
        modifier: utils.round(0),
        onUpdate: () => {
          node.textContent = String(obj.v);
        },
      });
    });
    if (!prefersReducedMotion()) {
      animate(ref.current.querySelectorAll(".api-stat"), { opacity: [0, 1], translateY: [14, 0], delay: stagger(110), duration: 600, ease: "outCubic" });
    }
  });
  return (
    <div ref={ref} className="grid grid-cols-3 gap-3 max-sm:grid-cols-1">
      {HOME_STATS.map((s) => (
        <div key={s.label} className="api-stat rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <p className="m-0 text-4xl font-bold text-white max-sm:text-3xl">
            <span data-count={s.to}>0</span>
            {s.suffix}
          </p>
          <p className="m-0 mt-1 text-sm text-gray-400">{s.label}</p>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Home page "Networks" section — Nodefleet network services, the chains each
// serves, and the Datanodes API quickstart. Static catalog only: no live
// probes or health/availability signals are shown on the landing.
// ---------------------------------------------------------------------------
const NetworksSection = () => {

  const sui = FEATURED_CHAINS.find((c) => c.slug === "sui");
  const others = FEATURED_CHAINS.filter((c) => c.slug !== "sui");

  return (
    <section id="networks" className="relative w-full overflow-hidden bg-space py-28 text-white max-sm:py-20">
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="bg-drift absolute inset-y-0 left-0 flex h-full" style={{ width: "200%", animationDuration: "110s" }}>
          <div className="relative h-full w-1/2 shrink-0">
            <StarField count={40} />
          </div>
          <div className="relative h-full w-1/2 shrink-0">
            <StarField count={40} />
          </div>
        </div>
      </div>
      <FloatingOrb size={480} color="rgba(90, 169, 230, 0.16)" style={{ top: -160, left: "-8%" }} duration={24} />
      <FloatingOrb size={360} color="rgba(153, 223, 175, 0.16)" style={{ bottom: -120, right: "-4%" }} duration={20} />

      <div className="relative z-10 mx-auto max-w-7xl px-8 max-sm:px-6">
        <Reveal className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-primary">Networks</Reveal>
        <Reveal as="h2" delay={0.05} className="mt-3 text-center text-5xl font-bold max-sm:text-3xl">
          Every network. <span className="text-brand-gradient italic">One fleet.</span>
        </Reveal>
        <Reveal delay={0.1} className="mx-auto mt-4 max-w-2xl text-center text-lg text-gray-400 max-sm:text-sm">
          Three services run the fleet: Datanodes serves the RPC traffic, Nodegate launches and operates chains, and Nodefleet Metric publishes the
          metrics.
        </Reveal>

        {/* Services */}
        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {SERVICES.map((svc, i) => (
            <ServiceCard key={svc.id} service={svc} index={i} />
          ))}
        </div>

        {/* Datanodes API quickstart */}
        <div className="mt-20 grid items-start gap-10 lg:grid-cols-[1.15fr_0.85fr]">
          <Reveal delay={0.1}>
            <Terminal />
          </Reveal>
          <div className="flex flex-col gap-6">
            <Stats />
            <Reveal delay={0.1} className="flex flex-wrap gap-2">
              {STATS.interfaces.map((i) => (
                <span key={i} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 font-mono text-xs text-gray-300">
                  {i}
                </span>
              ))}
            </Reveal>
            <Reveal delay={0.15} className="flex flex-wrap gap-3">
              <Link to="/networks" className="rounded-full bg-primary px-6 py-3 font-semibold text-space-deep no-underline shadow-glow transition-colors hover:bg-primary-400">
                Open the Networks reference
              </Link>
              <a
                href={GATEWAY.dashboard}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full border border-white/20 px-6 py-3 font-semibold text-white no-underline transition-colors hover:border-white/40 hover:bg-white/5"
              >
                Get an API key
              </a>
            </Reveal>
          </div>
        </div>

        {/* Chain references */}
        <Reveal delay={0.1} className="mt-20 flex flex-wrap items-end justify-between gap-4">
          <h3 className="m-0 text-3xl font-bold max-sm:text-2xl">
            Chain <span className="text-brand-gradient italic">references</span>
          </h3>
          <Link to="/networks" className="text-sm font-semibold text-primary no-underline hover:underline">
            Browse all {STATS.networks} networks →
          </Link>
        </Reveal>

        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          {sui && (
            <Reveal delay={0.1} className="lg:row-span-2">
              <Link
                to={`/networks/${sui.slug}`}
                className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-accent/30 bg-white/[0.03] p-8 no-underline transition-all hover:-translate-y-1 hover:border-accent/60"
              >
                <span className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
                <span className="mb-6 flex items-center justify-between">
                  <ChainLogo chain={sui} size={56} />
                  <span className="rounded-full bg-accent/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-accent">Full guide</span>
                </span>
                <span className="text-2xl font-bold text-white group-hover:text-accent">Sui API Reference</span>
                <span className="mt-2 text-sm text-gray-400">
                  Authenticate once, then query Sui over GraphQL or gRPC — endpoints, available methods, rate limits and the migration off legacy JSON-RPC.
                </span>
                <span className="mt-6 flex flex-wrap gap-2">
                  {["GraphQL", "gRPC", "JSON-RPC (legacy)"].map((t) => (
                    <span key={t} className="rounded-md bg-accent/15 px-2 py-1 font-mono text-[11px] text-accent">
                      {t}
                    </span>
                  ))}
                </span>
                <span className="mt-auto pt-8 text-sm font-semibold text-accent transition-transform group-hover:translate-x-1">Read the Sui reference →</span>
              </Link>
            </Reveal>
          )}
          {others.map((c, i) => {
            const fam = FAMILIES[c.family];
            return (
              <Reveal key={c.slug} delay={0.12 + i * 0.05}>
                <Link
                  to={`/networks/${c.slug}`}
                  className="group flex h-full items-center gap-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6 no-underline transition-all hover:-translate-y-1 hover:border-primary/40"
                >
                  <ChainLogo chain={c} size={48} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-lg font-bold text-white group-hover:text-primary">{c.name}</span>
                    <span className="block text-xs text-gray-400">{fam.interface}</span>
                    <span className="mt-1 block font-mono text-[11px] text-gray-500">x-chain: {c.slug}</span>
                  </span>
                  <span className="text-primary transition-transform group-hover:translate-x-1">→</span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default NetworksSection;
